import React, { useState, useRef, useEffect } from "react";
import "./NestedMachineSelector.css";
import FieldHelp from "../help/FieldHelp";

const NestedMachineSelector = ({ hierarchyData, selectedNode, onSelect, isFormMode = false }) => {
  // activePath: array of node names that are currently expanded at each level
  // index 0 = Main, index 1 = Branch, index 2 = Section
  const [activePath, setActivePath] = useState([]);
  const containerRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setActivePath([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (node) => {
    onSelect(node);
    // Behave like a navbar: clicking a link closes the menu
    setActivePath([]);
  };

  // Hover Handler (Desktop)
  const handleMouseEnter = (node, level) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    setActivePath((prev) => {
      // Keep path up to current level, replace/add current node
      const newPath = prev.slice(0, level);
      newPath[level] = node.name;
      return newPath;
    });
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActivePath([]);
    }, 300);
  };

  // Auto-reposition dropdowns when they mount
  const handleRootMenuMount = (el) => {
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.right > window.innerWidth - 10) {
        el.style.left = 'auto';
        el.style.right = '0';
        el.style.transformOrigin = 'top right';
      }
    }
  };

  const handleSubmenuMount = (el) => {
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.right > window.innerWidth - 10) {
        el.style.left = 'auto';
        el.style.right = '100%';
        el.style.marginRight = '6px';
        el.style.marginLeft = '0';
        el.style.transformOrigin = 'center right';
      }
    }
  };

  // Mobile/Click Handler for Arrow
  const toggleNode = (e, node, level) => {
    e.stopPropagation();
    setActivePath((prev) => {
      // If currently open at this level, close it
      if (prev[level] === node.name) {
        return prev.slice(0, level);
      }
      // Else open it
      const newPath = prev.slice(0, level);
      newPath[level] = node.name;
      return newPath;
    });
  };

  const getCategoryColor = (cat) => {
    if (!cat) return "#94a3b8";
    const c = cat.toUpperCase();
    if (c === "GRID") return "#0d9488"; // Teal
    if (c === "SOLAR") return "#f59e0b"; // Amber
    if (c === "GENERATOR") return "#ef4444"; // Red
    return "#64748b"; // Slate
  };

  // Called as a plain function (not <CategoryBadge />): it is redefined on every
  // render, so mounting it as a component would remount it on each hover.
  const CategoryBadge = ({ category }) => {
    if (!category || category === "Structure" || category === "Unknown")
      return null;
    return (
      <span
        style={{
          fontSize: "9px",
          fontWeight: 700,
          color: getCategoryColor(category),
          backgroundColor: `${getCategoryColor(category)}15`,
          padding: "2px 6px",
          borderRadius: "4px",
          marginLeft: "8px",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        <FieldHelp id="customization.nodeCategory">{category}</FieldHelp>
      </span>
    );
  };

  const renderMenuItem = (node, level) => {
    const hasChildren = node.children && node.children.length > 0;
    // Match by meter_id if available, otherwise fallback to name
    const matchesSelected = selectedNode && ((node.meter_id && selectedNode.meter_id === node.meter_id) || selectedNode.name === node.name || selectedNode === node.name);
    const isOpen = activePath[level] === node.name;

    return (
      <div
        key={node.name}
        className={`nested-menu-item ${matchesSelected ? "selected" : ""} ${isOpen ? "expanded" : ""}`}
        onMouseEnter={() => handleMouseEnter(node, level)}
      >
        <div
          className="menu-item-content"
          onClick={(e) => {
            e.stopPropagation();
            handleSelect(node);
          }}
        >
          <span className="node-name">{node.name}</span>
          {level === 0 && CategoryBadge({ category: node.category })}
        </div>

        {hasChildren && (
          <div
            className="arrow-container"
            onClick={(e) => toggleNode(e, node, level)}
          >
            <span className="arrow-right">›</span>
          </div>
        )}

        {hasChildren && isOpen && (
          <div className="nested-submenu" ref={handleSubmenuMount}>
            {node.children.map((child) => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!hierarchyData || hierarchyData.length === 0) {
    return null;
  }

  return (
    <div
      className={`nested-selector-container ${isFormMode ? 'form-mode' : ''}`}
      ref={containerRef}
      onMouseLeave={handleMouseLeave}
    >
      {hierarchyData.map((node) => {
        // Match by meter_id if available, otherwise by name string backward compatibility
        const isActive = selectedNode && ((node.meter_id && selectedNode.meter_id === node.meter_id) || selectedNode.name === node.name || selectedNode === node.name);
        const hasChildren = node.children && node.children.length > 0;
        const isOpen = activePath[0] === node.name; // Root level check

        return (
          <div
            key={node.name}
            className={`navbar-item ${isActive ? "active" : ""} ${isOpen ? "open" : ""}`}
            onMouseEnter={() => handleMouseEnter(node, 0)}
          >
            <div className="navbar-label" onClick={() => handleSelect(node)}>
              {node.name}
              {CategoryBadge({ category: node.category })}
            </div>

            {hasChildren && (
              <span
                className="dropdown-trigger"
                onClick={(e) => toggleNode(e, node, 0)}
              >
                ▼
              </span>
            )}

            {hasChildren && isOpen && (
              <div
                className="nested-dropdown-menu"
                ref={handleRootMenuMount}
              >
                {node.children.map((child) => renderMenuItem(child, 1))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default NestedMachineSelector;
